import React from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  buildInvitationUrl,
  startInvitationAuthorizationRequest,
} from '../../../../services/authentication/zitadel-invitation';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../../utils/variables';
import { Button } from '../../button/button';
import { InfoBox } from '../../info-box/info-box';
import { Stack } from '../../stack/stack';
import { TermsOfServiceCheckBox } from '../../terms-of-service-check-box/terms-of-service-check-box';
import { Form } from '../styles';
import { InvitationAccepted } from './invitation-accepted';

type Form = {
  tosChecked: boolean;
};

enum Status {
  Idle,
  InProgress,
  Success,
  Error,
}

type Props = {
  requestId: string;
  companyName: string;
};

const ExistingCompanyAdminForm = (props: Props) => {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState(Status.Idle);

  const { handleSubmit, control } = useForm<Form>();

  const acceptInvitation = async () => {
    try {
      setStatus(Status.InProgress);
      const companyService = SkyMapAxiosServiceFactory.instance.createUserServiceV0();
      const response = await companyService.acceptCompanyAdminRequest({
        body: {
          requestId: props.requestId,
        },
      });

      // Present only for a person still pending on Zitadel's side, who has not chosen a
      // password yet. See new-company-admin-form.tsx and verify-form.tsx for the same handoff.
      const setPasswordUrl = response.setPassword?.url;
      if (isDefined(setPasswordUrl)) {
        const requestId = await startInvitationAuthorizationRequest();
        window.location.replace(buildInvitationUrl(setPasswordUrl, requestId));
        return;
      }

      setStatus(Status.Success);
    } catch (err) {
      setStatus(Status.Error);
    }
  };

  const renderAcceptView = () => {
    return (
      <Form onSubmit={handleSubmit(acceptInvitation)}>
        <InfoBox bottomMargin={true} color="yellow">
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="newCompanyAdmin.existingUser.info"
            ns="components"
            values={{ companyName: props.companyName }}
          />
        </InfoBox>

        <Stack direction="column" spacing={1.5}>
          <TermsOfServiceCheckBox disabled={status === Status.InProgress} formControl={control} />

          {status === Status.Error && (
            <InfoBox color="red" leftIcon={{ icon: ['fad', 'check'] }}>
              {t('registerCompany.step3.registrationFailure', { ns: 'components' })}
            </InfoBox>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
            <Button
              color="primary"
              loading={status === Status.InProgress}
              type="submit"
              variant={'contained'}
            >
              {t('newCompanyAdmin.existingUser.submitFormButton', { ns: 'components' })}
            </Button>
          </Stack>
        </Stack>
      </Form>
    );
  };

  return status === Status.Success ? (
    <InvitationAccepted companyName={props.companyName} />
  ) : (
    renderAcceptView()
  );
};

export { ExistingCompanyAdminForm };
